/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and international
 * treaties. Unauthorized use, reproduction, distribution, or modification of any content
 * from Knowledge for Decisions (K4D) is strictly prohibited. For inquiries regarding
 * the use or licensing of K4D's opyrighted materials, please contact us at
 * contact@k4d.com.br. "Knowledge for Decisions (K4D)" and the slogan "Using data to make
 * precise decisions" are registered trademarks of Knowledge for Decisions (K4D).
 */

/* generated using openapi-typescript-codegen -- do no edit */
/* istanbul ignore file */
/* tslint:disable */
/* eslint-disable */
import type { ApiRequestOptions } from "./ApiRequestOptions";
import type { OpenAPIConfig } from "./OpenAPI";
import { isDefined } from "./request-guards";

// Helper function to append query parameter
/**
 * appendQueryParam function.
 *
 * @param {*} qs - Parameter description
 * @param {*} key - Parameter description
 * @param {*} value - Parameter description
 *
 * @returns {*} Return value description
 */
function appendQueryParam(qs: string[], key: string, value: any): void {
  qs.push(`${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`);
}

// Helper function to process array values
/**
 * processArrayValue function.
 *
 * @param {*} qs - Parameter description
 * @param {*} key - Parameter description
 * @param {*} value - Parameter description
 * @param {*} processFn - Parameter description
 * @param {*} value - Parameter description
 *
 * @returns {*} Return value description
 */
function processArrayValue(qs: string[], key: string, value: any[], processFn: (key: string, value: any) => void): void {
  value.forEach((v) => {
    processFn(key, v);
  });
}

// Helper function to process object values
/**
 * processObjectValue function.
 *
 * @param {*} qs - Parameter description
 * @param {*} key - Parameter description
 * @param {*} value - Parameter description
 * @param {*} any> - Parameter description
 * @param {*} processFn - Parameter description
 * @param {*} value - Parameter description
 *
 * @returns {*} Return value description
 */
function processObjectValue(qs: string[], key: string, value: Record<string, any>, processFn: (key: string, value: any) => void): void {
  Object.entries(value).forEach(([k, v]) => {
    processFn(`${key}[${k}]`, v);
  });
}

// Helper function to process array value
/**
 * processArrayValueForQuery function.
 *
 * @param {*} qs - Parameter description
 * @param {*} key - Parameter description
 * @param {*} value - Parameter description
 * @param {*} processFn - Parameter description
 * @param {*} value - Parameter description
 *
 * @returns {*} Return value description
 */
function processArrayValueForQuery(qs: string[], key: string, value: any[], processFn: (key: string, value: any) => void): void {
  processArrayValue(qs, key, value, processFn);
}

// Helper function to process object value
/**
 * processObjectValueForQuery function.
 *
 * @param {*} qs - Parameter description
 * @param {*} key - Parameter description
 * @param {*} value - Parameter description
 * @param {*} any> - Parameter description
 * @param {*} processFn - Parameter description
 * @param {*} value - Parameter description
 *
 * @returns {*} Return value description
 */
function processObjectValueForQuery(qs: string[], key: string, value: Record<string, any>, processFn: (key: string, value: any) => void): void {
  processObjectValue(qs, key, value, processFn);
}

// Helper function to process primitive value
/**
 * processPrimitiveValueForQuery function.
 *
 * @param {*} qs - Parameter description
 * @param {*} key - Parameter description
 * @param {*} value - Parameter description
 *
 * @returns {*} Return value description
 */
function processPrimitiveValueForQuery(qs: string[], key: string, value: any): void {
  appendQueryParam(qs, key, value);
}

// Helper function to process a single parameter value
/**
 * processValue function.
 *
 * @param {*} qs - Parameter description
 * @param {*} key - Parameter description
 * @param {*} value - Parameter description
 * @param {*} processFn - Parameter description
 * @param {*} value - Parameter description
 *
 * @returns {*} Return value description
 */
function processValue(qs: string[], key: string, value: any, processFn: (key: string, value: any) => void): void {
  if (!isDefined(value)) {
    return;
  }

  if (Array.isArray(value)) {
    processArrayValueForQuery(qs, key, value, processFn);
    return;
  }

  if (typeof value === "object") {
    processObjectValueForQuery(qs, key, value, processFn);
    return;
  }

  processPrimitiveValueForQuery(qs, key, value);
}

/**
 * getQueryString function.
 *
 * @param {*} params - Parameter description
 * @param {*} any> - Parameter description
 *
 * @returns {*} Return value description
 */
export const getQueryString = (params: Record<string, any>): string => {
  const qs: string[] = [];

  const process = (key: string, value: any) => {
    processValue(qs, key, value, process);
  };

  Object.entries(params).forEach(([key, value]) => {
    process(key, value);
  });

  return qs.length > 0 ? `?${qs.join("&")}` : "";
};

/**
 * getUrl function.
 *
 * @param {*} config - Parameter description
 * @param {*} options - Parameter description
 *
 * @returns {*} Return value description
 */
export const getUrl = (config: OpenAPIConfig, options: ApiRequestOptions): string => {
  const encoder = config.ENCODE_PATH || encodeURI;

  const path = options.url
    .replace("{api-version}", config.VERSION)
    .replace(/{(.*?)}/g, (substring: string, group: string) => {
      if (options.path?.hasOwnProperty(group)) {
        return encoder(String(options.path[group]));
      }
      return substring;
    });

  let url = `${config.BASE}${path}`;
  if (options.query) {
    url = `${url}${getQueryString(options.query)}`;
  }

  return url;
};
